import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import GalleryIcon from '@/assets/all-icons/gallery-icon';
import { getUnregisteredUserConfig } from '@/config/unregisteredUser.config';
import useStateHandler from '@/hooks/useStateHandler';
import AuthService from '@/services/auth.service';
import MandatoryPopupResultsService from '@/services/mandatoryPopupResults.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import ClaimArtistIcon from '@/shared-components/icons/components/user-profile/ClaimArtistIcon';
import TextInput from '@/shared-components/inputs/components/new/TextInput';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import Text from '@/shared-components/text/components/Text';

import AuthGoogle from './AuthGoogle';

import './styles/AuthRegularSignup.css';

export default function AuthRegularSignup({ close, setCurrentPage }) {
  const { state, stateHandler } = useStateHandler();
  const [formData, setFormData] = useState({
    user_email: "",
    user_password: "",
    user_confirm_password: ""
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const unregisteredUserConfig = getUnregisteredUserConfig();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prefillEmail = params.get("email") || params.get("claim_email");

    if (!prefillEmail) {
      return;
    }

    setFormData((prev) => {
      if (prev.user_email) {
        return prev;
      }
      return {
        ...prev,
        user_email: prefillEmail.trim(),
      };
    });
  }, [location.search]);

  const onInputChange = (e) => {
    const value = e.target.value;
    const key = e.target.name;
    setFormData({
      ...formData,
      [key]: value,
    });
    setError(null);
  };

  const continueButtonDisabled =
    !formData.user_email ||
    !formData.user_password ||
    !formData.user_confirm_password ||
    !termsAccepted ||
    error ;

  async function signUpSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if(!termsAccepted) {
      setError({ element: "user_terms", error_code: "TERMS_NOT_ACCEPTED" });
      setLoading(false);
      return;
    }

    if(e.target.user_password.value !== e.target.user_confirm_password.value) {
      setError({ element: "user_confirm_password", error_code: "PASSWORD_MISMATCH" });
      setLoading(false);
      return;
    }

    const { response, data } = await AuthService.registerCollector(e.target.user_email.value, e.target.user_password.value, state.invite);

    if(response.ok) {
      try {
        await AuthService.initUserSession(stateHandler, setLoading, data);
        MandatoryPopupResultsService.createMandatoryPopupResult(
          { result: "signup_success" },
          { useStoredContext: true, clearStoredContext: true }
        ).catch((error) => {
          console.error('Failed to persist mandatory popup signup success', error);
        });
      } catch (error) {
        console.error('Failed to initialize user session', error);
        alert('We could not complete your sign up. Please try again.');
        return;
      }
    }else if(response.status === 400) {
      setLoading(false);
      setError({ element: data.error_data.element, error_code: data.error_type });
    }
  }

  const onRequestGalleryAccessClick = () => {
    window.open("https://join.suarte.art/form", "_blank");
  };

  const onCollectorClick = () => {
    setCurrentPage("sign-up-artist");
  };

  // Build header message, supporting highlight for {username}
  const renderHeader = () => {
    const val = state.unregisteredUser;
    const cfg = unregisteredUserConfig;
    if (val && typeof val === 'string') {
      return cfg[val] || cfg.guest_rest;
    }
    if (val && Array.isArray(val)) {
      const [key, param] = val;
      if (key === 'guest_follow' || key === 'guest_artlist_like' || key === 'guest_artlist_play') {
        const tpl = cfg[key] || '';
        const parts = tpl.split('{username}');
        return (
          <>
            {parts[0]}
            <span className="auth-regular-signup__highlight">{param}</span>
            {parts[1]}
          </>
        );
      }
      if (key === 'guest_purchase') {
        return (cfg[key] || '').replace('{arwork_name}', param);
      }
    }
    return cfg.guest_rest;
  };

  return (
    <>
      <div className="auth-regular_header-container">
        <Text className="auth-regular-signup__or font-size-small">
          {renderHeader()}
        </Text>
      </div>
      <form onSubmit={signUpSubmit} className="auth-regular-signup__form">
        <TextInput
          error={error}
          type="email"
          label="Email"
          placeholder='Email'
          name="user_email"
          value={formData.user_email}
          element="user_email"
          onChange={onInputChange}
        />
        <TextInput
          error={error}
          type="password"
          label="Password"
          placeholder='Password'
          name="user_password"
          value={formData.user_password}
          element="user_password"
          onChange={onInputChange}
        />
        {formData.user_password && formData.user_password.length > 0 && (
          <TextInput
            error={error}
            type="confirmPassword"
            label="Confirm password"
            placeholder='Confirm password'
            name="user_confirm_password"
            value={formData.user_confirm_password}
            element="user_confirm_password"
            onChange={onInputChange}
          />
        )}

        <PublicFormInput error={error} className="auth-regular-signup_accept-conditions" element="user_terms" type="checkbox" value={termsAccepted} onChange={value => setTermsAccepted(value)}>
          I agree to the <span className="auth-regular-signup__terms" onClick={()=>navigate("/terms", { state: { from: true } })}>Terms and Conditions</span> and confirm I am 18 or older.
        </PublicFormInput>

        <ContinueButton loading={loading} turnOff={continueButtonDisabled} className="auth-regular-signup__submit-button">
          Continue
        </ContinueButton>
      </form>
      <Text className="auth-regular-signup__or font-size-small">If you prefer, you can</Text>
      <AuthGoogle
        className="auth-regular-signup__google-button"
        close={close}
        textButton='Sign up with Google'
      />
      <div className="auth-signup-selector__container" onClick={onRequestGalleryAccessClick}>
        <div className="auth-signup-selector__icon-box">
          <GalleryIcon className="auth-sign-up-selector__icon"/>
        </div>

        <Text className="auth-signup-selector__text font-size-medium">
          Request Gallery Access
        </Text>
      </div>
      <div className="auth-signup-selector__container" onClick={onCollectorClick}>
        <div className="auth-signup-selector__icon-box">
          <ClaimArtistIcon className="auth-sign-up-selector__icon"/>
        </div>

        <Text className="auth-signup-selector__text font-size-medium">
                    Claim Artist Profile
        </Text>

      </div>
    </>
  );
}
